import classNames from 'classnames';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import React, {ReactElement} from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {Entry} from 'notebook/ELNConstants';
import {isLocalSyncFile} from 'notebook/notes/attachments/NoteLinkedFileUtil';
import {DocumentEditorLock} from 'notebook/realtime/reduceDocumentEditorLockState';
import CreateUpdateReviewModalClass from 'notebook/reviews/CreateUpdateReviewModal';
import {mapReviewCriteriaGroupsToProps} from 'notebook/reviews/reduceReviewCriteria';
import ReviewConstants, {ReviewAction, ReviewSummary} from 'notebook/reviews/ReviewConstants';
import {
  REVIEW_CRITERIA_GROUP_SHAPE,
  REVIEW_CRITERION_STATUS,
  REVIEW_SHAPE,
  ReviewCriteriaGroup,
} from 'notebook/reviews/ReviewCriteriaConstants';
import ReviewStatusModal from 'notebook/reviews/ReviewStatusModal';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import {EntryNote} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import DocumentEditorLockUtil from 'realtime/util/DocumentEditorLockUtil';
import {BenchlingState} from 'redux-stores/reduceApp';
import EntityStore from 'stores/EntityStore';
// @ts-expect-error
import EntryStore from 'stores/EntryStore';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {ToggleDisplayMode} from 'ui-library/floating-ui/Float';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import APIUtil from 'util/APIUtil';
import {OngoingTaskResponse} from 'util/TaskRunner';
import * as StageEntryActions from 'workflows/stage-entries/StageEntryActions';
import StageEntryStore from 'workflows/stage-entries/StageEntryStore';
import {StageEntryDocument} from 'workflows/WorkflowConstants';

import {getLegacyReviewActionState, getLegacyReviewEditorLockTooltip} from './disableAuthorActions';
import {fetchReviewCriteria} from './ReviewCriteriaReduxActions';

function mapStateToProps(state: BenchlingState, {fileId}: {fileId: string}): Partial<EnhancedProps> {
  if (!BenchlingConfig.ENABLE_ELN_REVIEW_CRITERIA) {
    return {
      reviewCriteriaGroups: [],
      ...DocumentEditorLockUtil.mapDocumentEditorLockStateToProps(state, fileId),
    };
  }
  return {
    reviewCriteriaGroups: mapReviewCriteriaGroupsToProps(state, fileId),
    ...DocumentEditorLockUtil.mapDocumentEditorLockStateToProps(state, fileId),
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapEntryStoreToProps(entryStore: any, props: {fileId: string}): Partial<EnhancedProps> {
  let entry = null;
  let entryHasUnsavedChanges = null;
  if (entryStore.inStore(props.fileId)) {
    entry = entryStore.getEditorState(props.fileId).entry;
    entryHasUnsavedChanges = entryStore.hasUnsavedChanges(props.fileId);
  }
  return {entry, entryHasUnsavedChanges};
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapStageEntryStoreToProps(stageEntryStore: any, props: {fileId: string}): Partial<EnhancedProps> {
  let stageEntryDocument = null;
  let stageEntryHasUnsavedChanges = null;
  if (stageEntryStore.inStore(props.fileId)) {
    stageEntryDocument = stageEntryStore.getEditorState(props.fileId).stageEntryDocument;
    stageEntryHasUnsavedChanges = stageEntryStore.hasUnsavedChanges(props.fileId);
  }
  return {stageEntryDocument, stageEntryHasUnsavedChanges};
}

export function ReviewButton(props: {
  className?: string;
  disabled: boolean;
  fullWidth?: boolean;
  onClick: (action: ReviewAction) => void; // TODO
  action: ReviewAction;
  size: ButtonSize;
  text: string;
  dataTestAttrs?: DataTestAttrs;
}): JSX.Element {
  return (
    <Button
      buttonStyle={ReviewUtil.actionToButtonStyle(props.action)}
      className={props.className}
      disabled={props.disabled}
      fullWidth={props.fullWidth}
      icon={ReviewUtil.getIconNameFromAction(props.action)}
      onClick={() => {
        props.onClick(props.action);
      }}
      size={props.size}
      text={props.text}
      dataTestAttrs={props.dataTestAttrs}
    />
  );
}

interface PartialButtonProps {
  className?: string;
  fullWidth?: boolean;
  size: ButtonSize;
}

export interface ReviewButtonGroupProps {
  className?: string;
  buttonClassName?: string;
  fileId: string;
  fullWidth?: boolean;
  isVertical?: boolean;
  onUpdate: (
    _fileId: string,
    review: ReviewSummary,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ) => void;
  review?: ReviewSummary;
  size: ButtonSize;
  entryReviewLifecycleInfo?: {
    projectUsesReviewLifecycle: boolean;
    wasLastSentForLegacyReview: boolean;
  };
  // An optional higher order component that can be used to wrap the review buttons when they are rendered.
  wrapperComponent?: React.ComponentType;
}

interface EnhancedProps {
  reviewCriteriaGroups?: Array<ReviewCriteriaGroup> | null;
  entry?: Entry;
  entryHasUnsavedChanges?: boolean | null;
  stageEntryDocument?: StageEntryDocument;
  stageEntryHasUnsavedChanges?: boolean | null;
  fetchReviewCriteria: (linkedItemId: string) => void;
  // Tracks the current editor of the document
  documentEditorLock?: DocumentEditorLock;
}

export type ConnectedReviewButtonGroupProps = ReviewButtonGroupProps & EnhancedProps;

export const REVIEW_LEGACY_OR_REVIEW_LIFECYCLE_QUERY = gql`
  query ReviewLegacyOrReviewLifecycleQuery($reviewId: StrictString!) @skipAudit {
    review(id: $reviewId) {
      ... on ReviewNode {
        id
        reviewLifecycleVersion {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }

  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export class ReviewButtonGroup extends React.Component<ConnectedReviewButtonGroupProps> {
  static propTypes = {
    className: PropTypes.string,
    buttonClassName: PropTypes.string,
    fileId: PropTypes.string.isRequired,
    fullWidth: PropTypes.bool,
    isVertical: PropTypes.bool,
    onUpdate: PropTypes.func,
    review: REVIEW_SHAPE,
    reviewCriteriaGroups: PropTypes.arrayOf(REVIEW_CRITERIA_GROUP_SHAPE.isRequired),
    size: PropTypes.oneOf(Object.values(ButtonSize)).isRequired,
    entry: PropTypes.object,
    entryHasUnsavedChanges: PropTypes.bool,
    stageEntryDocument: PropTypes.object,
    stageEntryHasUnsavedChanges: PropTypes.bool,
    entryReviewLifecycleInfo: PropTypes.shape({
      projectUsesReviewLifecycle: PropTypes.bool,
      wasLastSentForLegacyReview: PropTypes.bool,
    }),
    wrapperComponent: PropTypes.func,
  };

  static defaultProps = {
    wrapperComponent: ({children}: {children: ReactElement | null}): ReactElement | null => children,
  };

  override componentDidMount(): void {
    this.props.fetchReviewCriteria(this.props.fileId);
  }

  _hasUnsavedChanges(): boolean {
    return Boolean(this.props.entryHasUnsavedChanges || this.props.stageEntryHasUnsavedChanges);
  }

  isLockedToOtherClient(): boolean {
    return this.props.documentEditorLock
      ? DocumentEditorLockUtil.isLockedInRealtime(this.props.documentEditorLock)
      : false;
  }

  _isInitialSendForReview(review?: ReviewSummary): boolean {
    const reviewRetractedBySystemChange =
      review?.auditors.length === 0 && review?.status === ReviewStatus.RETRACTED;
    return review == null || reviewRetractedBySystemChange;
  }

  onStatusChange = (action: ReviewAction): void => {
    if (
      !assertAndContinue(
        this.props.review != null && this.props.reviewCriteriaGroups != null,
        'onStatusChange called with null review data',
        {review: this.props.review, reviewCriteriaGroups: this.props.reviewCriteriaGroups}
      )
    ) {
      return;
    }

    if (action === ReviewAction.ACCEPT) {
      const anyCriteriaGroupsUnreviewed = this.props.reviewCriteriaGroups!.some(({reviewCriteria}) =>
        reviewCriteria.some((criteria) => criteria.status === REVIEW_CRITERION_STATUS.UNREVIEWED)
      );
      if (anyCriteriaGroupsUnreviewed) {
        notifications.error('All review criteria must be marked before accepting.');
        return;
      }
    }

    ReviewStatusModal.open({
      onUpdate: this.props.onUpdate,
      fileId: this.props.fileId,
      reviewId: this.props.review!.id,
      reviewCriteriaGroups: this.props.reviewCriteriaGroups!,
      action,
      message: ReviewUtil.getDefaultReviewComment(action),
    });
  };

  _handleUpdate = (
    _fileId: string,
    review: ReviewSummary,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ): void => {
    if (this.props.onUpdate) {
      this.props.onUpdate(_fileId, review, filePermissions, snapshotTask);
    }
    const fileType = APIUtil.apiIdToType(this.props.fileId);
    if (fileType === ModelTypeConstants.STAGE_ENTRY && review.status === ReviewConstants.ACCEPTED) {
      StageEntryActions.fetchInputDataForChildrenRuns(this.props.fileId);
    }
  };

  _isUserAuthor(): boolean {
    const entry = this.props.entry || this.props.stageEntryDocument;
    if (!entry) {
      return false;
    }
    for (const user of entry.authors) {
      if (user.handle === EntityStore.currentUser().handle) {
        return true;
      }
    }
    return false;
  }

  _renderSelfApproveButton(
    permissions: Permissions,
    {
      buttonProps,
      isBlocked,
      tooltipTitle,
      review,
    }: {
      buttonProps: PartialButtonProps;
      isBlocked: boolean;
      tooltipTitle: string | null;
      review?: ReviewSummary;
    }
  ): JSX.Element | null {
    if (!permissions.canAccept || !this._isUserAuthor()) {
      return null;
    }
    const effectiveTooltipTitle =
      tooltipTitle ??
      (this.isLockedToOtherClient() ? getLegacyReviewEditorLockTooltip(this.props.documentEditorLock) : null);
    return (
      <Tooltip
        text={effectiveTooltipTitle ?? ''}
        isDisabled={effectiveTooltipTitle == null}
        key="selfApproveButton"
        toggleDisplayMode={ToggleDisplayMode.BLOCK}
      >
        <Button
          {...buttonProps}
          className={classNames(buttonProps.className, {
            'u-small-spacing-bottom': this.props.isVertical,
          })}
          disabled={isBlocked}
          buttonStyle={ButtonStyle.PRIMARY}
          onClick={() =>
            review
              ? // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
                ReviewStatusModal.open({
                  reviewId: review.id,
                  fileId: this.props.fileId,
                  reviewCriteriaGroups: this.props.reviewCriteriaGroups!,
                  action: ReviewAction.ACCEPT,
                  onUpdate: this._handleUpdate,
                  title: 'Accepting',
                  message: ReviewUtil.getDefaultReviewComment(ReviewAction.ACCEPT),
                })
              : CreateUpdateReviewModalClass.open({
                  fileId: this.props.fileId,
                  onUpdate: this._handleUpdate,
                  review: this.props.review,
                  isSelfReview: true,
                })
          }
          text="Approve"
          dataTestAttrs={{component: 'ReviewAction', element: 'approve'}}
        />
      </Tooltip>
    );
  }

  _renderSendForReviewButton({
    buttonProps,
    isBlocked,
    tooltipTitle,
    review,
  }: {
    buttonProps: PartialButtonProps;
    isBlocked: boolean;
    tooltipTitle: string | null;
    review?: ReviewSummary;
  }): JSX.Element {
    // If the entry previously had a review but was retracted due to a review process change (ex: RLC -> Legacy vice versa),
    // we'll want to resend the review with the new auditors that the user fills in.
    if (this._isInitialSendForReview(review)) {
      return (
        <Tooltip
          text={tooltipTitle ?? ''}
          isDisabled={tooltipTitle == null}
          key="sendForReviewButton"
          toggleDisplayMode={ToggleDisplayMode.BLOCK}
        >
          <Button
            {...buttonProps}
            disabled={isBlocked}
            buttonStyle={ButtonStyle.SECONDARY}
            onClick={() => {
              CreateUpdateReviewModalClass.open({
                fileId: this.props.fileId,
                onUpdate: this._handleUpdate,
                review,
                isSelfReview: false,
              });
            }}
            text="Send for review"
            dataTestAttrs={{component: 'ReviewAction', element: 'sendForReview'}}
          />
        </Tooltip>
      );
    } else {
      return (
        <Tooltip
          text={tooltipTitle ?? ''}
          isDisabled={tooltipTitle == null}
          key="sendForReviewButton"
          toggleDisplayMode={ToggleDisplayMode.BLOCK}
        >
          <Button
            {...buttonProps}
            disabled={isBlocked}
            buttonStyle={ButtonStyle.SECONDARY}
            onClick={() => {
              ReviewStatusModal.open({
                reviewId: review!.id,
                fileId: this.props.fileId,
                reviewCriteriaGroups: this.props.reviewCriteriaGroups!,
                action: ReviewAction.SEND_FOR_REVIEW,
                onUpdate: this._handleUpdate,
                title: 'Resending for review',
                message: ReviewUtil.getDefaultReviewComment(ReviewAction.SEND_FOR_REVIEW),
              });
            }}
            text="Resend for review"
            dataTestAttrs={{component: 'ReviewAction', element: 'resendForReview'}}
          />
        </Tooltip>
      );
    }
  }

  _renderButtonsForReview(
    permissions: Permissions,
    {buttonProps, disableButtons}: {buttonProps: PartialButtonProps; disableButtons: boolean}
  ): JSX.Element | null {
    const buttons = [];
    if (permissions.canAccept && !this._isUserAuthor()) {
      buttons.push(
        <ReviewButton
          {...buttonProps}
          key="approveButton"
          disabled={disableButtons}
          action={ReviewAction.ACCEPT}
          text="Approve"
          onClick={this.onStatusChange}
          dataTestAttrs={{component: 'ReviewAction', element: 'approve'}}
        />
      );
    }
    if (permissions.canReject) {
      buttons.push(
        <React.Fragment key="rejectButton">
          <ReviewButton
            {...buttonProps}
            disabled={disableButtons}
            action={ReviewAction.REJECT}
            text="Reject"
            onClick={this.onStatusChange}
            dataTestAttrs={{component: 'ReviewAction', element: 'reject'}}
          />
        </React.Fragment>
      );
    }
    if (permissions.canRetract) {
      buttons.push(
        <React.Fragment key="retractButton">
          <ReviewButton
            {...buttonProps}
            disabled={disableButtons}
            action={ReviewAction.RETRACT}
            text="Retract"
            onClick={this.onStatusChange}
            dataTestAttrs={{component: 'ReviewAction', element: 'retract'}}
          />
        </React.Fragment>
      );
    }

    return buttons.length > 0 ? <React.Fragment key="buttonsForReview">{buttons}</React.Fragment> : null;
  }

  getButtonsForLegacyReview({
    review,
    buttonProps,
    reviewCriteriaGroupsLoading,
    buttonTooltipTitle,
    selfApproveIsBlocked,
    sendForReviewIsBlocked,
    permissions,
  }: {
    review?: ReviewSummary;
    buttonProps: {className?: string; fullWidth?: boolean; size: ButtonSize};
    reviewCriteriaGroupsLoading: boolean;
    buttonTooltipTitle: string | null;
    selfApproveIsBlocked: boolean;
    sendForReviewIsBlocked: boolean;
    permissions: Permissions;
  }): Array<JSX.Element> {
    const buttons = [];

    // Legacy reviews must only allow comments if this flag is enabled. RLC reviews do not care about the flag.
    if (review && BenchlingConfig.ENABLE_ELN_REVIEW_COMMENTS) {
      buttons.push(
        <ReviewButton
          {...buttonProps}
          key="commentButton"
          disabled={reviewCriteriaGroupsLoading}
          action={ReviewAction.COMMENT}
          text="Comment"
          onClick={this.onStatusChange}
          dataTestAttrs={{component: 'ReviewAction', element: 'comment'}}
        />
      );
    }
    const selfApproveButton = this._renderSelfApproveButton(permissions, {
      buttonProps,
      isBlocked: selfApproveIsBlocked,
      tooltipTitle: buttonTooltipTitle,
      review,
    });
    if (selfApproveButton) {
      buttons.push(selfApproveButton);
    }

    if (permissions.canSendForReview) {
      buttons.push(
        this._renderSendForReviewButton({
          buttonProps,
          isBlocked: sendForReviewIsBlocked,
          tooltipTitle: buttonTooltipTitle,
          review,
        })
      );
    } else if (review != null) {
      const buttonsForReview = this._renderButtonsForReview(permissions, {
        buttonProps,
        disableButtons: reviewCriteriaGroupsLoading,
      });
      if (buttonsForReview != null) {
        buttons.push(buttonsForReview);
      }
    }

    return buttons;
  }

  override render(): JSX.Element | null {
    const {review, reviewCriteriaGroups, entryReviewLifecycleInfo} = this.props;
    const entryOrStageEntry = this.props.entry || this.props.stageEntryDocument;
    if (entryOrStageEntry == null) {
      return null;
    }
    const permissions = entryOrStageEntry.permissions;

    const note: EntryNote | undefined =
      (this.props.entry && this.props.entry.note) ||
      (this.props.stageEntryDocument && this.props.stageEntryDocument.entryVersion.note);
    // eslint-disable-next-line no-restricted-syntax
    const hasLocalSyncFiles = note && _.some(note.noteParts, (notePart) => isLocalSyncFile(notePart.item));

    // If review in progress, don't allow interacting until reviewCriteriaGroups have loaded
    const disableButtons = review != null && reviewCriteriaGroups == null;

    const isInitialSend = this._isInitialSendForReview(review);

    const {tooltipTitle, isBlocked: sendForReviewIsBlocked} = getLegacyReviewActionState({
      note,
      hasLocalSyncFiles: Boolean(hasLocalSyncFiles),
      hasUnsavedChanges: this._hasUnsavedChanges(),
      isLockedToOtherClient: this.isLockedToOtherClient() && isInitialSend,
      reviewCriteriaGroupsLoading: disableButtons,
      documentEditorLock: this.props.documentEditorLock,
    });
    const selfApproveIsBlocked = sendForReviewIsBlocked || this.isLockedToOtherClient();

    const buttonProps = {
      className: this.props.buttonClassName,
      fullWidth: this.props.fullWidth,
      size: this.props.size,
    };
    const WrapperComponent = this.props.wrapperComponent!;

    const entryCanBeSentForReviewAndProjectHasRLC =
      Boolean(entryReviewLifecycleInfo?.projectUsesReviewLifecycle) &&
      ReviewUtil.canSendForReviewFromStatus(review?.status);
    const entryInReviewOrAcceptedWithRLCReview =
      entryReviewLifecycleInfo != null &&
      !entryReviewLifecycleInfo.wasLastSentForLegacyReview &&
      !ReviewUtil.canSendForReviewFromStatus(review?.status);

    // For (entries that are in progress, rejected, or retracted and the project has an RLC) OR (entries that
    // are in review using an RLC), we don't show legacy review actions, except comment
    // Detailed logic can be found here: https://docs.google.com/spreadsheets/d/16cnJY3GGem7WKa9ss5K9eMPDYjyb3eMtCFQr6lr2x0g/edit?usp=sharing
    if (entryCanBeSentForReviewAndProjectHasRLC || entryInReviewOrAcceptedWithRLCReview) {
      if (review == null) {
        return <WrapperComponent>{null}</WrapperComponent>;
      }

      // Only render the comment button when using RLC Reviews. Other RLC Review Actions live in the
      // ReviewLifecycleSummaryPanel.
      return (
        <WrapperComponent>
          <div className={this.props.className}>
            {/* Intentionally don't consider ENABLE_ELN_REVIEW_COMMENTS for review lifecycle reviews. */}
            <ReviewButton
              {...buttonProps}
              disabled={disableButtons}
              key="commentButton"
              action={ReviewAction.COMMENT}
              text="Comment"
              onClick={this.onStatusChange}
              dataTestAttrs={{component: 'ReviewAction', element: 'comment'}}
            />
          </div>
        </WrapperComponent>
      );
    }
    const buttons = this.getButtonsForLegacyReview({
      review,
      permissions,
      buttonTooltipTitle: tooltipTitle,
      buttonProps,
      reviewCriteriaGroupsLoading: disableButtons,
      selfApproveIsBlocked,
      sendForReviewIsBlocked,
    });

    return (
      <WrapperComponent>
        {buttons.length > 0 ? <div className={this.props.className}>{buttons}</div> : null}
      </WrapperComponent>
    );
  }
}

export default compose(
  EntryStore.connect(mapEntryStoreToProps),
  StageEntryStore.connect(mapStageEntryStoreToProps),
  connect(mapStateToProps, {fetchReviewCriteria})
)(ReviewButtonGroup);
